import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { useTransition, animated, config, useSpring } from 'react-spring';
import { Organization } from 'types';
import { useMeasure } from 'react-use';

import classes from './index.module.scss';
import { LoginInputs } from './LoginInputs';

import SignUpInputs from './SignUpInputs';
import { TfaInputs } from './TfaInputs';

import { fetchAllOrganizations } from '../../../api/organization';

type LoginFormProps = {
    stage: string;
    setStage: (stage: string) => void;
    loading: boolean;
    setError: (value: boolean) => void;
    loginError: boolean;
    signUpError: boolean;
    onSubmit: (credentials: { email: string; password: string, code: string }) => void;
    sendCode: (credentials: {
        email: string;
        prefer_text?: boolean;
    }) => Promise<{ message?: string }>;
    // onSignUp: (credentials: {
    //     email: string;
    //     password: string;
    //     passwordConfirmation: string;
    //     name: string;
    //     lastName: string;
    //     orgId: number | null;
    //     joinCode: string;
    //     phoneNumber: string;
    // }) => void;
    onSignUp: any;
    tfaRequired: boolean;
    isSignUpFlow: boolean;
    onCodeSent?: (message: JSX.Element | string) => void;
};

const LoginForm = ({
    stage,
    setStage,
    loading,
    setError,
    loginError,
    onSubmit,
    onSignUp,
    signUpError,
    tfaRequired,
    isSignUpFlow,
    sendCode,
    onCodeSent
}: LoginFormProps) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [code, setCode] = useState("");
    // sign up state
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [name, setName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [orgName, setOrgName] = useState('');
    const [orgList, setOrgList] = useState<Organization[]>([]);
    const [orgId, setOrgId] = useState<null | number>(null);
    const [joinCode, setJoinCode] = useState('');

    useEffect(() => {
        if (tfaRequired) {
            setStage('tfa');
        }
    }, [tfaRequired, setStage]);

    useEffect(() => {
        let isMounted = true;

        fetchAllOrganizations()
            .then((res) => res.json())
            .then((data) => {
                const visibleOrgs = (data.organizations || []).filter(
                    (org: Organization) => !org.is_hidden
                );

                if (isMounted) {
                    setOrgList(visibleOrgs);
                }
            })
            .catch((error) => {
                console.error('Failed to fetch organizations:', error);
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const passwordIsValid = useCallback((password: string) => {
        const passwordHasNumberOrSymbol = () => {
            const regex = /[0-9!@#$%^&*]/g;
            return regex.test(password);
        };

        const passwordHasUppercase = () => {
            const regex = /(?=.*[A-Z])(?=.*[a-z])/;
            return regex.test(password);
        };

        return (
            password.length >= 8 &&
            passwordHasNumberOrSymbol() &&
            passwordHasUppercase()
        );
    }, []);

    const inputsRef = useRef(null);

    const inputs = useMemo(
        () => (
            <LoginInputs
                stage={stage}
                setStage={setStage}
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                inputsRef={inputsRef}
                loading={loading}
                error={loginError}
            />
        ),
        [
            stage,
            email,
            password,
            loading,
            loginError
        ]
    );
    const tfaInputs = useMemo(
        () => (
            <TfaInputs
                isSignUpFlow={isSignUpFlow}
                email={email}
                inputsRef={inputsRef}
                loading={loading}
                setError={setError}
                error={loginError}
                code={code}
                setCode={setCode}
                sendCode={sendCode}
                onCodeSent={onCodeSent}
            />
        ),
        [
            email,
            loading,
            setError,
            loginError,
            sendCode,
            code,
            isSignUpFlow,
            onCodeSent
        ]
    );

    const signUpInputs = useMemo(
        () => (
            <SignUpInputs
                {...{
                    inputsRef,
                    setStage,
                    email,
                    setEmail,
                    password,
                    setPassword,
                    passwordConfirmation,
                    setPasswordConfirmation,
                    name,
                    setName,
                    lastName,
                    setLastName,
                    phoneNumber,
                    setPhoneNumber,
                    orgList,
                    orgName,
                    setOrgName,
                    orgId,
                    setOrgId,
                    joinCode,
                    setJoinCode,
                    passwordIsValid,
                    loading,
                    error: signUpError
                }}
            />
        ),
        [
            email,
            lastName,
            loading,
            name,
            orgList,
            orgName,
            orgId,
            joinCode,
            password,
            passwordConfirmation,
            phoneNumber,
            signUpError,
            passwordIsValid
        ]
    );

    // signin inputs an signup inputs use the same animation
    const inputsTransition = useTransition(
        stage === 'inputs' || stage === 'signUp' || stage === 'tfa',
        {
            from: {
                transform: 'translateX(50%)',
                opacity: 0
            },
            enter: {
                transform: 'translateX(-50%)',
                opacity: 1,
                zIndex: 1,
                left: '50%'
            },
            leave: {
                transform: 'translateX(20%)',
                opacity: 0,
                zIndex: 0
            },
            config: {
                ...config.stiff,
                clamp: true
            },
            unique: true
        }
    );

    const onSubmitCallback = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (loading) return;

        if (stage === 'signUp') {
            return onSignUp({
                email,
                password,
                passwordConfirmation,
                name,
                lastName,
                orgId,
                joinCode,
                phoneNumber
            });
        }
        if (stage === 'tfa') {
            return onSubmit({ email, password, code });
        }
        return onSubmit({ email, password, code: "" });
    };

    // make the height and width use spring
    const [measureRef, { height, width }] = useMeasure();
    const formStyle = useSpring({
        height,
        width: stage === 'tfa' ? 400 : width || 200,
        config: { clamp: true, tension: 300 }
    });

    const content =
        stage === 'signUp'
            ? signUpInputs
        : stage === 'tfa'
            ? tfaInputs
        : inputs;

    return (
        <animated.form
            className={classes.form}
            onSubmit={onSubmitCallback}
            autoComplete="off"
            style={formStyle}
        >
            {inputsTransition((props, item) =>
                item && (
                    <animated.div
                        style={{
                            ...props,
                            ...(stage === 'tfa' ? { width: '100%' } : {})
                        }}                        
                        key="a"
                        className={classes.animatedForm}
                        ref={measureRef as any}
                    >
                        {content}
                    </animated.div>
                )
            )}
        </animated.form>
    );
};

export default LoginForm;
